import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import _ from 'lodash';

export const shapeConfigurationProperties = PropTypes.shape({
  title: PropTypes.string,
});

export const shapeStudentAttempt = PropTypes.shape({
  attemptId: PropTypes.string.isRequired,
  componentId: PropTypes.string.isRequired,
  parentId: PropTypes.string.isRequired,
  parentType: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  studentId: PropTypes.string.isRequired,
});

export const shapeManualGradableComponent = PropTypes.shape({
  componentId: PropTypes.string.isRequired,
  deploymentId: PropTypes.string.isRequired,
  maxScore: PropTypes.number.isRequired,
  parentId: PropTypes.string.isRequired,
  parentType: PropTypes.string.isRequired,
  componentConfiguration: shapeConfigurationProperties,
  parentConfiguration: shapeConfigurationProperties,
});

export const shapeManualGradableDescendents = PropTypes.shape({

});

const getAncestryGQL = gql`
query ($workspaceId:UUID!, $elementId:UUID!) {
    workspace(workspaceId: $workspaceId) {
        id
        getCoursewareAncestry(elementId: $elementId) {
            elementId
            type
            ancestry {
                elementId
                elementType
            }
        }
    }
}
`;

const learnPerformanceByActivityGQL = gql`
query ($cohortId: UUID!, $studentId: UUID!, $deploymentId: UUID!, $activityId: UUID!) {
  cohortById(cohortId: $cohortId) {
    id
    enrollmentByStudent(studentId: $studentId) {
      learnerPerformanceByDeployment(activityId: $activityId, deploymentId: $deploymentId) {
        getLatestAttemptActivityManualGradeReport {
          attemptId
          componentId
          parentId
          parentType
          state
          studentId
          grades {
            createdAt
            score
          }
        }
      }
    }
  }
}
`;

const manualGradingComponentsByDeploymentGQL = gql`
query getManualGradingReport($cohortId: UUID!, $deploymentId: UUID!) {
  cohortById(cohortId: $cohortId) {
    name
    id
    deployment(deploymentId: $deploymentId) {
      id
      manualGradingComponents {
        componentId
        deploymentId
        maxScore
        parentId
        parentType
        componentConfigurationFields(fieldNames: ["title"]) {
          fieldName
          fieldValue
        }
      }
    }
  }
}
`;

/* Export local functions into an object for no other reason than to simplify
 * and isolate tests.
 */
export const manualGradingSummaryUtilities = {
};

/* Fetch ancestry via apollo for a given elementId (which can be almost anything, but
 * in this case will most likely be a componentId
 * Requires a workspaceId, which is supplied in the cohortSummary payload.
 */
export const queryGetAncestry = async (apolloClient, { workspaceId, elementId }, filterTypes) => {
  const response = await apolloClient.query({
    query: getAncestryGQL,
    variables: {
      workspaceId,
      elementId,
    } });

  if (response.error) {
    throw new Error(response.error);
  }

  const parsed = _.get(response, 'data.workspace.getCoursewareAncestry.ancestry');

  if (filterTypes) {
    const filtered = parsed.filter(_.memoize(({ elementType }) => filterTypes.includes(elementType)));
    return filtered;
  }

  return parsed;
};

/* Queries all grade reports of an activity's descendents,
 * belonging to a specified student
 */
export const queryGetLearnerPerformanceByActivity = async (apolloClient, { cohortId, deploymentId, activityId, studentId }) => {
  const response = await apolloClient.query({
    query: learnPerformanceByActivityGQL,
    variables: {
      cohortId,
      deploymentId,
      activityId,
      studentId,
    },
    errorPolicy: 'all',
  });

  if (response.error) {
    throw new Error(response.error);
  }

  const getLatestScore = (grades = []) => (grades.length > 0 ?
    grades.sort((a, b) => +Date.parse(b.createdAt) - +Date.parse(a.createdAt))[0].score :
    null);

  const parsed = _.chain(response)
    .get('data.cohortById.enrollmentByStudent.learnerPerformanceByDeployment.getLatestAttemptActivityManualGradeReport')
    .map(({ grades, ...rest }) => ({ ...rest, score: getLatestScore(grades) }))
    .value();

  return parsed;
};

/* Needed for descriptions and maxscores
*/
export const queryGetGradableComponentsByDeploymentId = async (apolloClient, { cohortId, deploymentId }) => {
  const response = await apolloClient.query({
    query: manualGradingComponentsByDeploymentGQL,
    variables: {
      cohortId,
      deploymentId,
    },
  });

  if (response.error) {
    throw new Error(response.error);
  }

  const fieldCollectionToObj = (fields) => (
    _.chain(fields).keyBy('fieldName').mapValues('fieldValue').value());

  const parsed = _.chain(response)
    // Filter through the cruft to our data
    .get('data.cohortById.deployment')
    // Flatten the one thing we are interested in
    .flatMap('manualGradingComponents')
    // convert configuration fields into an object so it makes sense later
    .map(({ componentConfigurationFields, ...rest }) => ({
      ...rest, componentConfiguration: fieldCollectionToObj(componentConfigurationFields),
    }))
    .value();

  return parsed;
};

/*
 * Its possible that there are parents of our activity, and we dont care
 * about them for this function
 */
export const trimAncestryToActivityId = (ancestry, activityId) => {
  const index = ancestry.findIndex(({ elementId }) => elementId === activityId);
  return ancestry.slice(0, index + 1);
};

/*
 * get an ancestry array between two element Ids (descendantId & ancestorId)
 * results look like: child <-> [{ elementId }, { elementType }, ...] <-> parent
 *
 * Memoizing should prevent the function being run a silly amount of times, but could
 * present problems if the ancestry changes in the same session becuase we never
 * invalidate the memoize cache.  HOWEVER, it is very unlikely that (impossible?)
 * the ancstry would change while an instructor is grading students.
 */
export const traceAncestryBetween = _.memoize(async ({ apolloClient, workspaceId, descendantId, ancestorId }) => {
  const ancestry = await manualGradingSummaryUtilities.queryGetAncestry(apolloClient,
    { workspaceId, elementId: descendantId }, ['ACTIVITY', 'INTERACTIVE']);
  return trimAncestryToActivityId(ancestry, ancestorId);
  // construct the cache key out of parentId and walkableId
}, ({ descendantId, ancestorId }) => ancestorId + descendantId);

manualGradingSummaryUtilities.queryGetAncestry = queryGetAncestry;
manualGradingSummaryUtilities.queryGetGradableComponentsByDeploymentId = queryGetGradableComponentsByDeploymentId;
manualGradingSummaryUtilities.queryGetLearnerPerformanceByActivity = queryGetLearnerPerformanceByActivity;
manualGradingSummaryUtilities.traceAncestryBetween = traceAncestryBetween;

/*
 * Get our grade report
 * return looks like:
 * {
 *  directManualGrades: [ { gradableComponent, gradeReport }]
 *  children: {
 *    'walkableId': [ { gradableComponent, gradeReport }]
 *  }
 * }
 */
export const getGradeSummaryByActivity = async (apolloClient, workspaceId, cohortId, deploymentId, activityId, studentId) => {
  const gradableComponents = await manualGradingSummaryUtilities.queryGetGradableComponentsByDeploymentId(apolloClient, { cohortId, deploymentId });
  const gradeAttemptReports = await manualGradingSummaryUtilities.queryGetLearnerPerformanceByActivity(apolloClient, { cohortId, deploymentId, activityId, studentId });

  // we are going to be doing quite a few lookups here, so lets pick an
  // efficient data structure for the task
  const keyedGradables = gradableComponents.reduce((dictionary, component) => ({ ...dictionary, [component.componentId]: component }), {});

  // add each gradereport to bucket of ids.
  const sortedGradeReports = await gradeAttemptReports.reduce(async (bucket, gradeReport) => {
    const gradableComponent = keyedGradables[gradeReport.componentId];

    // Since this is probably a promise from the previous iteration
    const resolvedBucket = await bucket;

    let ancestry;
    try {
      ancestry = await manualGradingSummaryUtilities.traceAncestryBetween({ apolloClient, workspaceId, descendantId: gradableComponent.parentId, ancestorId: activityId });
    } catch (error) {
      // Ancestry has failed for some reason. Most likely this is because the gradeable component
      // was removed from the interactive or activity. If that's the case, skip it.
      return resolvedBucket;
    }

    // The object that we will merge into our return bucket.
    const gradeSummaryEntry = { gradableComponent, gradeReport };

    // This manual grade is a direct child of the activity
    if (ancestry.length === 1) {
      return { ...resolvedBucket, directManualGrades: [...resolvedBucket.directManualGrades, gradeSummaryEntry] };
    }

    // Manualgrade belongs to a child.  Sumarize this into the immediate children.
    if (ancestry.length > 1) {
      // use the second last element to construct a child id.
      const childId = ancestry.slice(-2, -1)[0].elementId;
      const existingChildBucket = resolvedBucket.children[childId] || [];
      return { ...resolvedBucket, children: { ...resolvedBucket.children, [childId]: [...existingChildBucket, gradeSummaryEntry] } };
    }

    throw new Error('Not enough ancestry');
  }, { directManualGrades: [], children: {} });

  return sortedGradeReports;
};

